import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { ILocationInfo, IPatientInfo } from "src/app-telehealth-demo/util/utils";
import DevOnlyButton from "src/components/library/everyone/DevOnlyButton";
import { IS_LOBBIE_DEV, IS_LOBBIE_LOCAL } from "src/constants";
import { STATE_OPTIONS } from "src/constants/regions";
import * as yup from "yup";

interface PatientInfoStepProps {
    patientInfo: Partial<IPatientInfo>;
    updatePatientInfo: (data: Partial<IPatientInfo>) => void;
    locationInfo: ILocationInfo; // The conversion data from previous step
    onContinue: () => void;
    onBack: () => void;
}

// Form validation schema
const schema = yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    dateOfBirth: yup.string().required("Date of birth is required"),
    email: yup.string().email("Please enter a valid email").required("Email is required"),
    phone: yup
        .string()
        .matches(/^\d{10}$/, "Phone number must be 10 digits")
        .required("Phone number is required"),
    address: yup.string().required("Address is required"),
    address2: yup.string().notRequired(),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zip: yup
        .string()
        .matches(/^\d{5}(-\d{4})?$/, "ZIP code must be 5 digits or 5+4 format")
        .required("ZIP code is required"),
});

const PatientInfoStep: React.FC<PatientInfoStepProps> = ({
    patientInfo,
    updatePatientInfo,
    locationInfo,
    onContinue,
    onBack,
}) => {
    const [error, setError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
        defaultValues: {
            firstName: patientInfo.firstName || "",
            lastName: patientInfo.lastName || "",
            dateOfBirth: patientInfo.dateOfBirth || "",
            email: patientInfo.email || "",
            phone: patientInfo.phone || "",
            address: patientInfo.address || locationInfo?.address || "",
            address2: patientInfo.address2 || "",
            city: patientInfo.city || locationInfo.city || "",
            state: patientInfo.state || locationInfo?.state || "",
            zip: patientInfo.zip || locationInfo.zip || "",
        },
    });

    const fillWithFakeData = () => {
        const fakeData = {
            firstName: "John",
            lastName: "Smith",
            dateOfBirth: "1985-06-15",
            email: "john.smith@example.com",
            phone: "3035551234",
            address: "1310 Chuparosa Way",
            city: "Carlsbad",
            state: "CA",
            zip: "92008",
        };

        // Set values in the form
        Object.entries(fakeData).forEach(([field, value]) => {
            setValue(field as keyof typeof fakeData, value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
            });
        });

        // Update the parent component state
        updatePatientInfo(fakeData);
    };

    // Update the parent component state when form values change
    useEffect(() => {
        const subscription = watch((value) => {
            updatePatientInfo({
                firstName: value.firstName,
                lastName: value.lastName,
                dateOfBirth: value.dateOfBirth,
                email: value.email,
                phone: value.phone,
                address: value.address,
                address2: value.address2 ?? "",
                city: value.city,
                state: value.state,
                zip: value.zip,
            });
        });
        return () => subscription.unsubscribe();
    }, [watch, updatePatientInfo]);

    const onSubmit = (data: IPatientInfo) => {
        // Update patient info in parent component
        updatePatientInfo(data);
        // Continue to next step
        onContinue();
    };

    return (
        <div className="patient-info-step">
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4">Patient Information</h2>
                {(IS_LOBBIE_LOCAL || IS_LOBBIE_DEV) && (
                    <DevOnlyButton onClick={fillWithFakeData}>Fill with Test Data</DevOnlyButton>
                )}
            </div>
            <p className="text-muted mb-4">
                Please provide your personal information to continue with your appointment.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                <Card className="mb-4">
                    <Card.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.firstName}
                                        {...register("firstName")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.firstName?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.lastName}
                                        {...register("lastName")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.lastName?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date of Birth</Form.Label>
                                    <Form.Control
                                        type="date"
                                        isInvalid={!!errors.dateOfBirth}
                                        {...register("dateOfBirth")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.dateOfBirth?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        isInvalid={!!errors.email}
                                        {...register("email")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        placeholder="1234567890"
                                        isInvalid={!!errors.phone}
                                        {...register("phone")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.phone?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                                type="text"
                                isInvalid={!!errors.address}
                                {...register("address")}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.address?.message}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Address Line 2 (Optional)</Form.Label>
                            <Form.Control type="text" {...register("address2")} />
                        </Form.Group>

                        <Row>
                            <Col md={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <Form.Control
                                        type="text"
                                        isInvalid={!!errors.city}
                                        {...register("city")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.city?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>State</Form.Label>
                                    <Form.Select isInvalid={!!errors.state} {...register("state")}>
                                        <option value="">Select State</option>
                                        {STATE_OPTIONS.filter((option) => option.value).map(
                                            (state) => (
                                                <option key={state.value} value={state.value}>
                                                    {state.label}
                                                </option>
                                            ),
                                        )}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.state?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>ZIP Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="12345"
                                        isInvalid={!!errors.zip}
                                        {...register("zip")}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.zip?.message}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" onClick={onBack}>
                        Back
                    </Button>
                    <Button variant="primary" type="submit">
                        Continue
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default PatientInfoStep;
